/******************************************************************************
 * MachineDuplicateGroupRow: one duplicate group, collapsed to a summary row and
 * expandable to a survivor picker.
 *
 * Clustering groups by label similarity, so a group can carry a true negative --
 * a row that reads alike but is a different machine. Each non-survivor member
 * therefore has an include toggle: uncheck it and that row is left alone while
 * the rest of the group merges. The backend already accepts this, since
 * `mergedIds` is an explicit list it validates for existence and enabled-ness
 * rather than against the detected group.
 *
 * Merging is irreversible from the UI (the backend disables rows rather than
 * deleting them, but nothing here re-enables them), so it goes through a
 * confirmation dialog that names what is about to move. After a merge the tab
 * reloads from the server rather than patching local state: a merge shifts
 * machine counts and can dissolve neighbouring groups.
 *****************************************************************************/

/* Imports */
import { Fragment, useState } from "react";
import { t } from "i18next";
import toast from "react-hot-toast";
import { Button, Checkbox, Chip, Dialog, Radio, Typography } from "@material-tailwind/react";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
/***/

/* Services */
import { api } from "../../../../../../services/api/api";
import { showApiErrorToast } from "../../../../../../services/api/error";
import { EmptyMachineLabelError, renameMachineReferential } from "./renameMachineReferential";
import MachineLabelEditor from "./MachineLabelEditor";
/***/

/**
* MachineDuplicateGroupRow: a duplicate group
* @param group - the group to render
* @param kind - which list it belongs to
* @param onMerged - reload callback, invoked after a successful merge
*/
export default function MachineDuplicateGroupRow({
  group,
  kind,
  onMerged
}: {
  group: EasyTech.MachineDuplicateGroup,
  kind: EasyTech.MachineReferentialKind,
  onMerged: () => void
}) {
  const [expanded, setExpanded] = useState(false);
  const [survivorId, setSurvivorId] = useState<string | null>(group.suggestedSurvivorId);
  const [excludedIds, setExcludedIds] = useState<string[]>([]);
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const machines = group.members.reduce((total, member) => total + member.machineCount, 0);
  const survivor = group.members.find((member) => member.id === survivorId) || null;
  const merged = group.members.filter(
    (member) => member.id !== survivorId && !excludedIds.includes(member.id));
  const movingMachines = merged.reduce((total, member) => total + member.machineCount, 0);
  // survivorId is seeded once from group.suggestedSurvivorId and never re-synced, so a
  // stale value from a previous group shape must never be accepted: it would resolve
  // survivor to null, make merged the entire member list, and post an unrelated
  // survivorId with every member as mergedIds -- an irreversible wrong merge.
  const survivorIsMember = !!survivorId && group.members.some((member) => member.id === survivorId);

  /**
  * chooseSurvivor: make a member the survivor, re-including it if it was excluded.
  * A row cannot be both kept and left alone, and an excluded survivor would put
  * the group in a state the merge button could not describe.
  */
  const chooseSurvivor = (memberId: string) => {
    setSurvivorId(memberId);
    setExcludedIds((ids) => ids.filter((id) => id !== memberId));
  };

  /**
  * toggleIncluded: include or exclude a member from this merge
  */
  const toggleIncluded = (memberId: string) => {
    setExcludedIds((ids) => ids.includes(memberId)
      ? ids.filter((id) => id !== memberId)
      : [...ids, memberId]);
  };

  /**
  * submitMerge: keep the chosen row, retire every other member
  */
  /**
  * renameMember: correct one row's label, then reload. A rename that lands on
  * a label already in use is rejected by the API; that pair belongs in a merge.
  */
  const renameMember = (memberId: string, label: string) => {
    return renameMachineReferential(kind, memberId, label).then(() => {
      toast.success(t("admin:tools.machineDedup.rename.success"));
      onMerged();
    }).catch((err) => {
      if (err instanceof EmptyMachineLabelError) {
        toast.error(t("admin:tools.machineDedup.rename.empty"));
      } else {
        console.error(err);
        showApiErrorToast(err, { fallbackMessage: t("admin:tools.machineDedup.errors.rename") });
      }
      throw err;
    });
  };

  const submitMerge = () => {
    if (!survivorId || !group.members.some((m) => m.id === survivorId)) return;
    if (merged.length === 0) return;

    setSubmitting(true);
    api.post("admin/machine_duplicates/merge", {
      kind,
      survivorId,
      mergedIds: merged.map((member) => member.id)
    }).then(() => {
      toast.success(t("admin:tools.machineDedup.merge.success"));
      setConfirming(false);
      onMerged();
    }).catch((err) => {
      console.error(err);
      showApiErrorToast(err, { fallbackMessage: t("admin:tools.machineDedup.errors.merge") });
    }).finally(() => setSubmitting(false));
  };
  /***/

  return (
    <Fragment>
      <tr data-testid={"group-" + group.groupId}>
        <td>
          <button type="button"
            data-testid={"toggle-" + group.groupId}
            className="flex items-center gap-2 text-left"
            onClick={() => setExpanded((open) => !open)}>
            {expanded ? <ChevronDownIcon className="w-4" /> : <ChevronRightIcon className="w-4" />}
            <span>{group.members.map((member) => member.label).join(" · ")}</span>
          </button>
        </td>
        <td data-testid={"member-count-" + group.groupId}>{group.members.length}</td>
        <td data-testid={"machine-count-" + group.groupId}>{machines}</td>
        <td>
          <Chip size="sm"
            variant="ghost"
            color={group.requiresReview ? "amber" : "green"}
            value={t("admin:tools.machineDedup.confidence." + group.confidence)} />
        </td>
        <td>
          <Button size="sm" variant="text"
            data-testid={"open-" + group.groupId}
            onClick={() => setExpanded((open) => !open)}>
            {expanded
              ? t("admin:tools.machineDedup.hide")
              : t("admin:tools.machineDedup.view")}
          </Button>
        </td>
      </tr>

      {expanded && (
        <tr data-testid={"detail-" + group.groupId}>
          <td colSpan={5}>
            <div className="p-4 bg-blue-gray-50/40">
              {group.requiresReview && (
                <Typography variant="small" className="mb-3 text-amber-800"
                  data-testid={"review-notice-" + group.groupId}>
                  {t("admin:tools.machineDedup.reviewNotice")}
                </Typography>
              )}

              <Typography variant="small" className="mb-3 text-gray-700">
                {t("admin:tools.machineDedup.rename.hint")}
              </Typography>

              <Typography variant="small" className="mb-2 font-medium">
                {t("admin:tools.machineDedup.merge.pick")}
              </Typography>

              <ul className="flex flex-col gap-2">
                {group.members.map((member) => (
                  <li key={member.id} data-testid={"member-" + member.id}
                    className="flex items-center justify-between gap-4">
                    <label className="flex items-center gap-2">
                      <Radio name={"survivor-" + group.groupId}
                        data-testid={"survivor-" + member.id}
                        checked={survivorId === member.id}
                        onChange={() => chooseSurvivor(member.id)} />
                      <span>{member.label}</span>
                    </label>
                    <span className="flex items-center gap-4">
                      <MachineLabelEditor
                        label={member.label}
                        testId={member.id}
                        onSave={(label) => renameMember(member.id, label)} />
                      <span className="text-gray-600">
                        {t("admin:tools.machineDedup.machines", { count: member.machineCount })}
                      </span>
                      {survivorId !== member.id && (
                        <label className="flex items-center gap-1 text-gray-700">
                          <Checkbox
                            data-testid={"include-" + member.id}
                            checked={!excludedIds.includes(member.id)}
                            onChange={() => toggleIncluded(member.id)} />
                          {t("admin:tools.machineDedup.merge.include")}
                        </label>
                      )}
                    </span>
                  </li>
                ))}
              </ul>

              <div className="mt-4 flex justify-end">
                <Button size="sm" color="red"
                  data-testid={"merge-" + group.groupId}
                  disabled={!survivorIsMember || merged.length === 0 || submitting}
                  onClick={() => setConfirming(true)}>
                  {t("admin:tools.machineDedup.merge.button")}
                </Button>
              </div>
            </div>
          </td>
        </tr>
      )}

      <Dialog open={confirming} handler={() => setConfirming(false)} size="xs">
        <div className="p-4">
          <Typography variant="h5" className="mb-2">
            {t("admin:tools.machineDedup.merge.confirmTitle")}
          </Typography>
          <Typography className="mb-4">
            {t("admin:tools.machineDedup.merge.confirmBody", {
              survivor: survivor ? survivor.label : "",
              rows: merged.length,
              machines: movingMachines
            })}
          </Typography>
          <div className="flex justify-end gap-2">
            <Button variant="outlined" color="blue-gray" size="sm"
              data-testid={"cancel-merge-" + group.groupId}
              disabled={submitting}
              onClick={() => setConfirming(false)}>
              {t("common:cancel")}
            </Button>
            <Button color="red" size="sm"
              data-testid={"confirm-merge-" + group.groupId}
              disabled={submitting}
              onClick={submitMerge}>
              {t("admin:tools.machineDedup.merge.confirmButton")}
            </Button>
          </div>
        </div>
      </Dialog>
    </Fragment>
  );
}
/***/
